import { useState, useRef, useEffect } from 'react'

export const BADGE_HOVER_DELAY = 75

/**
 * Reusable hook for badge hover with delay + animation blocking.
 * Blocks hover events briefly after a value changes (appear animation).
 */
export function useBadgeHover<T>(value: T) {
  const [isHovered, setIsHovered] = useState(false)
  const blockedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Block hover during appear animation; clear hovered state when value removed.
  // Uses useEffect instead of render-phase side effects for React 18 compatibility.
  const prevValueRef = useRef(value)
  useEffect(() => {
    if (prevValueRef.current === value) return
    prevValueRef.current = value
    if (value !== null && value !== undefined) {
      if (blockTimerRef.current) clearTimeout(blockTimerRef.current)
      blockedRef.current = true
      blockTimerRef.current = setTimeout(() => { blockedRef.current = false }, 200)
    } else {
      setIsHovered(false)
    }
    return () => {
      if (blockTimerRef.current) clearTimeout(blockTimerRef.current)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [value])

  const onMouseEnter = (callback?: () => void) => {
    if (callback) {
      callback()
      return
    }
    if (!blockedRef.current) {
      timerRef.current = setTimeout(() => setIsHovered(true), BADGE_HOVER_DELAY)
    }
  }

  const onMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setIsHovered(false)
  }

  return { isHovered, setIsHovered, blockedRef, timerRef, onMouseEnter, onMouseLeave }
}
